// Listing Mapping settings strings — EN + TR.
//
// Wave-2 Day 2. Settings sub-section for SKU alias management (Wave 3 S8;
// alias_sku → master product). 573 LOC source file had TR/EN mix per i18n
// audit 2026-06-02 (commit ca624ba):
// - actions TR ("Yükleniyor / Çözümleniyor / Resolve")
// - headers EN ("Listing Mapping / Add mapping / SKU Resolver")
// - confirm modal Turkish + dialog body Turkish
//
// Channel names (Etsy / Shopify / Amazon / WooCommerce) are proper nouns
// and locale-invariant per Volkan ratify; only the "All" filter chip is
// translated.
//
// Source: features/settings/ListingMapping.tsx.

import type { LocalizedDict } from '../index'

type ListingMappingStrings = {
  pageTitle: string
  pageDescriptionLead: string
  s8InvariantLabel: string
  pageDescriptionTail: string
  addMapping: string
  nonCanonicalNotice: string
  footerNote: string
  resolver: {
    title: string
    hint: string
    placeholder: string
    resolve: string
    resolving: string
    resolvedBadge: (via: string) => string
    notFoundBadge: string
    notFoundTail: string
  }
  filters: {
    channelLabel: string
    all: string
    searchPlaceholder: string
  }
  columns: {
    channel: string
    aliasSku: string
    master: string
    storeListing: string
    created: string
  }
  states: {
    loading: string
    emptySearch: (query: string) => string
    emptyAll: string
  }
  table: {
    listingPrefix: string
  }
  deleteConfirm: (alias: string) => string
  toasts: {
    createdTitle: string
    createdDescription: string
    createFailed: string
    deletedTitle: string
    deletedDescription: string
    deleteFailed: string
    resolverFailed: string
  }
  modal: {
    title: string
    description: string
    masterProduct: string
    masterProductPlaceholder: string
    channel: string
    aliasSku: string
    aliasSkuPlaceholder: string
    namingHint: string
    storeIdLabel: string
    storeIdPlaceholder: string
    listingIdLabel: string
    listingIdPlaceholder: string
    cancel: string
    save: string
    saving: string
  }
}

export const listingMappingI18n: LocalizedDict<ListingMappingStrings> = {
  en: {
    pageTitle: 'Listing Mapping',
    pageDescriptionLead:
      'Map Etsy / Shopify / Amazon listing SKUs to PEXIFY master catalog products. ',
    s8InvariantLabel: 'S8 invariant:',
    pageDescriptionTail:
      ' the same alias_sku cannot be bound to two different masters. PEXIFY publishes register the alias automatically; use this surface to add a manual map for existing listings.',
    addMapping: 'Add mapping',
    nonCanonicalNotice:
      'These aliases are NOT used by order intake. Orders resolve SKUs via the seller_store_skus binding created by Catalog Sync + the admin Unmapped SKU queue. This screen manages legacy channel aliases (search / labeling) only.',
    footerNote:
      'Manual mapping flow · PEXIFY publish integration ratify in Wave 3.5+ M3 · CSV bulk import on the roadmap.',
    resolver: {
      title: 'SKU Resolver',
      hint: 'Type any master_sku or alias_sku to see which master product it resolves to',
      placeholder: 'LCL-CARD-BROWN or WOOD-ENGR-L',
      resolve: 'Resolve',
      resolving: 'Resolving…',
      resolvedBadge: (via) => `resolved · via ${via}`,
      notFoundBadge: 'not found',
      notFoundTail: '— no master_sku or alias matched',
    },
    filters: {
      channelLabel: 'Channel',
      all: 'All',
      searchPlaceholder: 'Search alias / master / store',
    },
    columns: {
      channel: 'Channel',
      aliasSku: 'Alias SKU',
      master: 'Master',
      storeListing: 'Store / Listing',
      created: 'Created',
    },
    states: {
      loading: 'Loading…',
      emptySearch: (query) => `No matches for "${query}".`,
      emptyAll: 'No mappings yet. Start with "Add mapping".',
    },
    table: {
      listingPrefix: 'listing #',
    },
    deleteConfirm: (alias) =>
      `Delete '${alias}' mapping? The alias will be released back to the global pool.`,
    toasts: {
      createdTitle: 'Listing mapped',
      createdDescription: 'Alias mapping added (S8 globally unique).',
      createFailed: 'Could not add mapping',
      deletedTitle: 'Mapping deleted',
      deletedDescription:
        "Alias released; you can now assign it to a different master.",
      deleteFailed: 'Delete failed',
      resolverFailed: 'Resolver failed',
    },
    modal: {
      title: 'Add listing mapping',
      description:
        "Bind an Etsy / Shopify listing SKU to a PEXIFY master product. alias_sku must be globally unique (same SKU cannot point at two masters).",
      masterProduct: 'Master product',
      masterProductPlaceholder: '— Select —',
      channel: 'Channel',
      aliasSku: 'Alias SKU (channel SKU)',
      aliasSkuPlaceholder: 'e.g. LCL-CARD-BROWN',
      namingHint:
        '💡 Naming convention tip: prefix to separate store/channel (LCL- = LeatherCraftLab).',
      storeIdLabel: 'External store ID (optional)',
      storeIdPlaceholder: 'sarah_etsy_main',
      listingIdLabel: 'Listing ID (optional)',
      listingIdPlaceholder: 'etsy_1480123',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving…',
    },
  },
  tr: {
    pageTitle: 'İlan Eşleştirme',
    pageDescriptionLead:
      'Etsy / Shopify / Amazon ilan SKU\'larını PEXIFY master katalog ürünlerine eşleştir. ',
    s8InvariantLabel: 'S8 invariant:',
    pageDescriptionTail:
      ' aynı alias_sku iki farklı master ürüne bağlanamaz. PEXIFY\'dan ilan açtığında otomatik kayıt olur; mevcut ilanların için buradan manuel eşleştirme ekle.',
    addMapping: 'Eşleştirme ekle',
    nonCanonicalNotice:
      'Buradaki alias\'lar sipariş alımında KULLANILMAZ. Siparişler SKU\'ları, Katalog Sync + admin Eşlenmemiş SKU kuyruğunun oluşturduğu seller_store_skus kaydı üzerinden çözer. Bu ekran yalnızca legacy kanal alias\'larını (arama / etiketleme) yönetir.',
    footerNote:
      'Manuel eşleştirme akışı · PEXIFY\'dan ilan yayınlama entegrasyonu Wave 3.5+ M3 ratify · CSV toplu içe aktarım roadmap\'te.',
    resolver: {
      title: 'SKU Çözümleyici',
      hint: 'Herhangi bir master_sku veya alias_sku gir, hangi master ürüne çözümlendiğini gör',
      placeholder: 'LCL-CARD-BROWN veya WOOD-ENGR-L',
      resolve: 'Çözümle',
      resolving: 'Çözümleniyor…',
      resolvedBadge: (via) => `çözümlendi · ${via} ile`,
      notFoundBadge: 'bulunamadı',
      notFoundTail: '— hiçbir master_sku veya alias eşleşmedi',
    },
    filters: {
      channelLabel: 'Kanal',
      all: 'Tümü',
      searchPlaceholder: 'Alias / master / mağaza ara',
    },
    columns: {
      channel: 'Kanal',
      aliasSku: 'Alias SKU',
      master: 'Master',
      storeListing: 'Mağaza / İlan',
      created: 'Eklendi',
    },
    states: {
      loading: 'Yükleniyor…',
      emptySearch: (query) => `"${query}" için eşleşme yok.`,
      emptyAll: 'Henüz eşleştirme yok. "Eşleştirme ekle" ile başla.',
    },
    table: {
      listingPrefix: 'ilan #',
    },
    deleteConfirm: (alias) =>
      `'${alias}' eşleştirmesini silmek istiyor musun? Alias global havuza geri serbest bırakılır.`,
    toasts: {
      createdTitle: 'İlan eşleştirildi',
      createdDescription: 'Alias eşleştirmesi eklendi (S8 global benzersiz).',
      createFailed: 'Eşleştirme eklenemedi',
      deletedTitle: 'Eşleştirme silindi',
      deletedDescription:
        'Alias serbest bırakıldı; başka bir master\'a atayabilirsin.',
      deleteFailed: 'Silme başarısız',
      resolverFailed: 'Çözümleyici çalışmadı',
    },
    modal: {
      title: 'İlan eşleştirmesi ekle',
      description:
        'Etsy / Shopify ilan SKU\'sunu PEXIFY master ürününe bağla. alias_sku global benzersiz olmalı (aynı SKU iki master\'a gidemez).',
      masterProduct: 'Master ürün',
      masterProductPlaceholder: '— Seç —',
      channel: 'Kanal',
      aliasSku: 'Alias SKU (kanal SKU)',
      aliasSkuPlaceholder: 'örn: LCL-CARD-BROWN',
      namingHint:
        '💡 Adlandırma kuralı önerisi: ön ek ile mağaza/kanal ayır (LCL- = LeatherCraftLab).',
      storeIdLabel: 'External store ID (opsiyonel)',
      storeIdPlaceholder: 'sarah_etsy_main',
      listingIdLabel: 'Listing ID (opsiyonel)',
      listingIdPlaceholder: 'etsy_1480123',
      cancel: 'İptal',
      save: 'Kaydet',
      saving: 'Kaydediliyor…',
    },
  },
}
